import React, { useState, useEffect, useCallback } from 'react';
import BackButton from '@/components/BackButton';
import { useLanguage } from '@/contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Briefcase, Eye, CheckCircle, XCircle, SpinnerGap } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "/branding/logo.png";

export default function GMRecruitment() {
  const { language, isRTL } = useLanguage();
  const navigate = useNavigate();
  const direction = isRTL ? 'rtl' : 'ltr';

  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDetail, setShowDetail] = useState(false);
  const [showAction, setShowAction] = useState(false);
  const [selected, setSelected] = useState(null);
  const [actionType, setActionType] = useState('');
  const [notes, setNotes] = useState('');
  const [acting, setActing] = useState(false);
  const [timeline, setTimeline] = useState([]);

  const fetchApplicants = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/recruitment/gm-applicants`, { withCredentials: true });
      setApplicants(data);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { fetchApplicants(); }, [fetchApplicants]);

  const DEPT_LABELS = {
    dentistry: 'طب الأسنان', nursing: 'التمريض', customer_service: 'خدمة العملاء',
    insurance: 'التأمين', administration: 'الإدارة', laboratory: 'المعمل',
    radiology: 'الأشعة', sterilization: 'التعقيم', finance: 'المالية',
    maintenance: 'إدارة المرافق والتشغيل', other: 'أخرى'
  };

  const openDetail = async (app) => {
    setSelected(app);
    setShowDetail(true);
    try {
      const { data } = await axios.get(`${API}/recruitment/applicants/${app.id}/timeline`, { withCredentials: true });
      setTimeline(data);
    } catch {}
  };

  const openAction = (app, type) => {
    setSelected(app);
    setActionType(type);
    setNotes('');
    setShowAction(true);
  };

  const handleAction = async () => {
    if (!selected) return;
    setActing(true);
    try {
      await axios.post(`${API}/recruitment/applicants/${selected.id}/action`, { action: actionType, notes }, { withCredentials: true });
      toast.success(language === 'ar' ? 'تم تنفيذ الإجراء بنجاح' : 'Action completed');
      setShowAction(false);
      fetchApplicants();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
    setActing(false);
  };

  if (loading) return <div className="flex items-center justify-center h-64"><SpinnerGap size={32} className="animate-spin text-[#655dc6]" /></div>;

  return (
    <div className="min-h-screen bg-[#F2F0FA]" dir={direction} data-testid="gm-recruitment-page">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-[#C4BFF0] py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-[#655dc6]" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto p-6 space-y-5">
        <h1 className="text-2xl font-bold text-[#655dc6]">
          <Briefcase size={26} weight="duotone" className="inline-block me-2 align-middle" />
          {language === 'ar' ? 'اعتمادات التوظيف' : 'Recruitment Approvals'}
        </h1>

        <Card className="border-[#C4BFF0]">
          <CardContent className="p-0">
            <Table data-testid="gm-applicants-table">
              <TableHeader>
                <TableRow className="border-[#C4BFF0]">
                  <TableHead className="text-start text-xs">{language === 'ar' ? 'الاسم' : 'Name'}</TableHead>
                  <TableHead className="text-center text-xs">{language === 'ar' ? 'المهنة' : 'Profession'}</TableHead>
                  <TableHead className="text-center text-xs">{language === 'ar' ? 'القسم' : 'Dept'}</TableHead>
                  <TableHead className="text-center text-xs">{language === 'ar' ? 'الخبرة' : 'Exp.'}</TableHead>
                  <TableHead className="text-center text-xs">{language === 'ar' ? 'الحالة' : 'Status'}</TableHead>
                  <TableHead className="text-center text-xs">{language === 'ar' ? 'الإجراءات' : 'Actions'}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {applicants.length === 0 ? (
                  <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#655dc6]/40">
                    {language === 'ar' ? 'لا توجد طلبات بانتظار اعتمادك' : 'No requests pending your approval'}
                  </TableCell></TableRow>
                ) : applicants.map(app => (
                  <TableRow key={app.id} data-testid={`gm-row-${app.id}`}>
                    <TableCell className="text-start font-medium text-[#655dc6]">{app.name_ar || app.name_en}</TableCell>
                    <TableCell className="text-center text-sm text-[#655dc6]/70">{app.profession}</TableCell>
                    <TableCell className="text-center text-xs">
                      <Badge variant="outline" className="border-[#C4BFF0] text-[#655dc6]">{DEPT_LABELS[app.department] || app.department}</Badge>
                    </TableCell>
                    <TableCell className="text-center text-sm">{app.years_of_experience || 0}</TableCell>
                    <TableCell className="text-center">
                      <Badge className="bg-teal-100 text-teal-700">{language === 'ar' ? 'معتمد من مدير القسم' : 'Dept Approved'}</Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex gap-1 justify-center">
                        <Button variant="ghost" size="sm" className="h-7 text-[#655dc6]" onClick={() => openDetail(app)}><Eye size={14} /></Button>
                        <Button size="sm" className="h-7 bg-green-600 hover:bg-green-700 text-white text-xs"
                          onClick={() => openAction(app, 'gm_approve')} data-testid={`gm-approve-${app.id}`}>
                          <CheckCircle size={14} className="me-1" /> {language === 'ar' ? 'اعتماد' : 'Approve'}
                        </Button>
                        <Button size="sm" variant="outline" className="h-7 text-red-500 border-red-200 text-xs"
                          onClick={() => openAction(app, 'reject')} data-testid={`gm-reject-${app.id}`}>
                          <XCircle size={14} className="me-1" /> {language === 'ar' ? 'رفض' : 'Reject'}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>

      {/* Detail + Timeline */}
      <Dialog open={showDetail} onOpenChange={setShowDetail}>
        <DialogContent className="sm:max-w-[500px]" dir={direction}>
          {selected && (
            <div dir={direction} style={{ direction }}>
              <DialogHeader>
                <DialogTitle className="text-[#655dc6]">{selected.name_ar || selected.name_en}</DialogTitle>
                <DialogDescription>{selected.profession} | {selected.nationality}</DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-[#655dc6]/50 text-xs">{language === 'ar' ? 'القسم' : 'Dept'}</span><br/>{DEPT_LABELS[selected.department] || selected.department}</div>
                  <div><span className="text-[#655dc6]/50 text-xs">{language === 'ar' ? 'الخبرة' : 'Exp.'}</span><br/>{selected.years_of_experience || 0}</div>
                  <div><span className="text-[#655dc6]/50 text-xs">{language === 'ar' ? 'المؤهل' : 'Education'}</span><br/>{selected.education || '-'}</div>
                  <div><span className="text-[#655dc6]/50 text-xs">{language === 'ar' ? 'الجنسية' : 'Nationality'}</span><br/>{selected.nationality || '-'}</div>
                </div>
                {selected.summary && <p className="text-sm bg-[#F2F0FA] p-2 rounded">{selected.summary}</p>}

                <p className="text-xs font-bold text-[#655dc6] mt-4">{language === 'ar' ? 'سجل التتبع' : 'Timeline'}</p>
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {timeline.map((t, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <div className="w-2 h-2 rounded-full bg-[#655dc6] mt-1.5 flex-shrink-0" />
                      <div className="text-xs">
                        <p className="font-semibold text-[#655dc6]">{language === 'ar' ? t.action_label_ar : t.action_label_en}</p>
                        <p className="text-[#655dc6]/50">{t.performer_name} - {new Date(t.created_at).toLocaleString('ar-SA', { timeZone: 'Asia/Riyadh' })}</p>
                        {t.notes && <p className="text-[#655dc6]/70 mt-0.5">{t.notes}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Action Dialog */}
      <Dialog open={showAction} onOpenChange={setShowAction}>
        <DialogContent className="sm:max-w-[400px]" dir={direction}>
          <div dir={direction} style={{ direction }}>
            <DialogHeader>
              <DialogTitle className={actionType === 'reject' ? 'text-red-600' : 'text-green-600'}>
                {actionType === 'reject' ? (language === 'ar' ? 'رفض المتقدم' : 'Reject') : (language === 'ar' ? 'اعتماد المدير العام' : 'GM Approval')}
              </DialogTitle>
              <DialogDescription>{selected?.name_ar || selected?.name_en}</DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-3">
              <Label>{language === 'ar' ? 'ملاحظات' : 'Notes'}</Label>
              <Textarea value={notes} onChange={e => setNotes(e.target.value)} className="min-h-[80px]" />
            </div>
            <DialogFooter className="mt-4">
              <Button onClick={handleAction} disabled={acting}
                className={actionType === 'reject' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}>
                {acting ? <SpinnerGap size={16} className="animate-spin" /> : (language === 'ar' ? 'تأكيد' : 'Confirm')}
              </Button>
              <Button variant="outline" onClick={() => setShowAction(false)}>{language === 'ar' ? 'إلغاء' : 'Cancel'}</Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
